/**
 * Member-only support layer for the "How are you feeling?" emotion wheel:
 * a short guided reset per feeling, plus extra reflection prompts so every
 * feeling offers 4–6 gentle questions.
 *
 * Language stays exploratory and non-diagnostic throughout.
 */

import { CORE_FEELINGS, type CoreFeeling } from "@/data/feelings-pathway";

export type Reset = {
  /** e.g. "60-Second Shame Reset" */
  title: string;
  /** Short spoken-style script, offered as an invitation and never an instruction. */
  script: string[];
};

export const RESETS: Record<string, Reset> = {
  shame: {
    title: "60-Second Shame Reset",
    script: [
      "Place both feet on the floor, if that feels comfortable.",
      "Notice three things around you that remind you that you are here, now.",
      "Place a hand somewhere that feels supportive, and take one slower breath out.",
      "Silently say: I am noticing shame. A feeling is not a definition of who I am.",
      "Ask yourself: what would kindness toward myself look like for the next five minutes?",
    ],
  },
  guilt: {
    title: "60-Second Guilt Reset",
    script: [
      "Let your shoulders drop a little, if they'll go.",
      "Name the one thing you're holding yourself responsible for — just to yourself.",
      "Ask: how much of this was actually within my control?",
      "Silently say: I can care about the impact without carrying all of it.",
      "If there's a repair to make, note it for later. If there isn't, let this be a moment of self-forgiveness.",
    ],
  },
  sad: {
    title: "60-Second Comfort Pause",
    script: [
      "Let yourself sit or lean against something solid.",
      "Notice where the sadness sits in your body, without needing to move it.",
      "Place a warm hand there for a few breaths.",
      "Silently say: this feeling makes sense, and it doesn't have to be fixed right now.",
      "Ask yourself: what would comfort look like for the next ten minutes?",
    ],
  },
  anxious: {
    title: "60-Second Grounding Reset",
    script: [
      "Press your feet into the floor and feel the ground taking your weight.",
      "Name five things you can see, then two things you can hear.",
      "Breathe in for a count of four, and out for a longer count of six — twice.",
      "Silently say: my body is trying to protect me. I can help it slow down.",
      "Ask yourself: what is one small thing that would feel a little safer right now?",
    ],
  },
  angry: {
    title: "60-Second Safe Discharge",
    script: [
      "If you can, stand up and push your palms firmly against a wall for ten seconds, then release.",
      "Shake out your hands and let your jaw unclench.",
      "Take one long breath out, as though blowing through a straw.",
      "Silently say: this anger is information. It doesn't have to be acted on right now.",
      "Ask yourself: what line was crossed, and what would protecting myself look like?",
    ],
  },
  lonely: {
    title: "60-Second Connection Pause",
    script: [
      "Place a hand on your chest and feel your own warmth there.",
      "Notice one thing in the room that belongs to you or was chosen by you.",
      "Silently say: I'm noticing loneliness. This feeling is not proof that I am unwanted.",
      "Bring to mind one person, animal or place where you have felt even slightly at ease.",
      "Ask yourself: is there one small point of contact I could make today, if I wanted to?",
    ],
  },
  rejected: {
    title: "60-Second Self-Compassion Reset",
    script: [
      "Let your body settle into whatever is holding you — chair, bed, floor.",
      "Silently say: this hurts. That makes sense.",
      "Notice the story attached to it, and see whether you can hold it lightly: this may or may not be the whole picture.",
      "Place a hand somewhere supportive and take one slower breath out.",
      "Ask yourself: what would I say to someone I love who had just been left out?",
    ],
  },
  overwhelmed: {
    title: "60-Second Nervous-System Reset",
    script: [
      "Let your eyes rest on one fixed point for a few seconds.",
      "Breathe out longer than you breathe in, three times.",
      "Let your shoulders and jaw soften as much as they will.",
      "Silently say: I don't have to hold everything at once.",
      "Ask yourself: what is the one next thing — and what can genuinely wait?",
    ],
  },
  numb: {
    title: "60-Second Gentle Orientation",
    script: [
      "Look slowly around the room and let your eyes land wherever they want.",
      "Notice the temperature of the air on your hands or face.",
      "Press your fingertips together lightly, one pair at a time.",
      "Silently say: numbness is a form of protection. There's no rush.",
      "Ask yourself: what is one small, low-effort sensation that feels tolerable right now?",
    ],
  },
  afraid: {
    title: "60-Second Safety Anchor",
    script: [
      "Look around and name three things that tell you where and when you are.",
      "Feel the floor, the chair, or the ground taking your weight.",
      "Breathe out slowly, twice, letting the out-breath be longer.",
      "Silently say: something in me is bracing. Right now, in this moment, I am here.",
      "Ask yourself: what would help my body feel even slightly safer — light, distance, a door, a person?",
    ],
  },
  disappointed: {
    title: "60-Second Acknowledgement Pause",
    script: [
      "Let yourself pause rather than move straight on.",
      "Name what you were hoping for, quietly and honestly.",
      "Silently say: it makes sense that this stings. Wanting something wasn't a mistake.",
      "Place a hand somewhere supportive and take one slower breath out.",
      "Ask yourself: what do I need before I decide anything about what comes next?",
    ],
  },
  unworthy: {
    title: "60-Second Worth Reset",
    script: [
      "Feel your feet on the floor and let your weight be held.",
      "Notice the harsh sentence running underneath, and say it back with: I'm noticing the thought that…",
      "Silently say: a thought about my worth is not a measurement of it.",
      "Bring to mind one thing you did recently that took effort, however small.",
      "Ask yourself: what would treating myself as if I mattered look like for the next hour?",
    ],
  },
};

/** Used for the body-first path and anywhere no feeling has been named. */
export const GENERIC_RESET: Reset = {
  title: "60-Second Gentle Reset",
  script: [
    "Let your feet rest on the floor and your weight be held.",
    "Look slowly around and name three things you can see.",
    "Breathe out a little longer than you breathe in, twice.",
    "Silently say: I don't need to name this to take care of myself.",
    "Ask yourself: what would feel even slightly kinder for the next few minutes?",
  ],
};

/** Extra prompts appended to each feeling so every one offers 4–6 questions. */
const EXTRA_PROMPTS: Record<string, string[]> = {
  shame: [
    "What happened just before this feeling appeared?",
    "Am I judging myself for something that was outside my control?",
    "What do I need to hear right now?",
  ],
  guilt: [
    "What happened just before this feeling appeared?",
    "Whose expectations am I measuring myself against?",
    "What would I want a friend to do in the same situation?",
  ],
  sad: [
    "What happened just before this feeling appeared?",
    "Does this sadness remind me of anything earlier?",
    "What would comfort look like today, in a small way?",
  ],
  anxious: [
    "What was happening just before the worry grew louder?",
    "What is my mind predicting, and how certain is that really?",
    "What would help my body feel a little safer right now?",
  ],
  angry: [
    "What happened just before this feeling appeared?",
    "What need or boundary of mine might have been crossed?",
    "What would protecting myself look like, without harming me or anyone else?",
  ],
  lonely: [
    "When did I last feel genuinely accompanied?",
    "Is there a story attached to this — about being too much, or not enough?",
    "What kind of connection would feel manageable today?",
  ],
  rejected: [
    "What happened just before this feeling appeared?",
    "What am I telling myself this means about me?",
    "What would I say to someone I cared about who felt this way?",
  ],
  overwhelmed: [
    "What has been asking the most of me lately?",
    "What am I carrying that was never mine to carry?",
    "What is the one next thing, and what can wait?",
  ],
  numb: [
    "When did I last notice a feeling clearly?",
    "What might my system be protecting me from right now?",
    "What is the smallest amount of sensation that feels tolerable?",
  ],
  afraid: [
    "What happened just before this feeling appeared?",
    "Does this fear belong to now, to earlier, or to both?",
    "What would help me feel even slightly safer in this moment?",
  ],
  disappointed: [
    "What was I hoping for?",
    "What does this disappointment tell me about what matters to me?",
    "What do I need before deciding anything about what comes next?",
  ],
  unworthy: [
    "Whose voice does this thought sound like?",
    "What evidence am I ignoring because it doesn't fit the thought?",
    "What would I say to someone I love who believed this about themselves?",
  ],
};

/** 4–6 reflection prompts for a feeling, in a gentle order. */
export function promptsFor(feeling: CoreFeeling): string[] {
  const extra = EXTRA_PROMPTS[feeling.key] ?? [];
  return [...feeling.prompts, ...extra].slice(0, 6);
}

export function resetFor(feeling: CoreFeeling | null): Reset {
  return (feeling && RESETS[feeling.key]) || GENERIC_RESET;
}

/** Every core feeling has a reset — guards against a future feeling being added without one. */
export const MISSING_RESETS = CORE_FEELINGS.filter((f) => !RESETS[f.key]).map((f) => f.key);
